<template>
  <div class="emoji-picker">
    <div class="emoji-picker__search">
      <Input
        v-model="searchQuery"
        type="text"
        class="emoji-picker__search-input"
        placeholder="Search emojis..."
        :small="true"
      />
    </div>
    <div class="emoji-picker__categories">
      <button
        v-for="category in categories"
        :key="category.id"
        class="emoji-picker__category-btn"
        :class="{ 'emoji-picker__category-btn--active': activeCategory === category.id }"
        @click="activeCategory = category.id"
        :title="category.name"
      >
        <Icon :icon="category.icon" />
      </button>
    </div>
    <div class="emoji-picker__content">
      <div v-if="searchQuery" class="emoji-picker__search-results">
        <button
          v-for="emoji in searchResults"
          :key="emoji.emoji"
          class="emoji-picker__emoji"
          @click="selectEmoji(emoji)"
          :title="emoji.name"
        >
          {{ emoji.emoji }}
        </button>
      </div>
      <div v-else class="emoji-picker__category-content">
        <div
          v-for="category in filteredCategories"
          :key="category.id"
          class="emoji-picker__category-section"
        >
          <h3 class="emoji-picker__category-title">{{ category.name }}</h3>
          <div class="emoji-picker__emoji-grid">
            <button
              v-for="emoji in category.emojis"
              :key="emoji.emoji"
              class="emoji-picker__emoji"
              @click="selectEmoji(emoji)"
              :title="emoji.name"
            >
              {{ emoji.emoji }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Input from "@/components/ui/Input.vue";
import { EMOJI_CATEGORIES } from "@/utils/emojis";
import { Icon } from "@iconify/vue";

export default {
  name: "EmojiPicker",
  components: {
    Icon,
    Input,
  },
  emits: ["select"],
  data() {
    return {
      searchQuery: "",
      activeCategory: "recent",
      categories: [
        { id: "recent", name: "Recently Used", icon: "mdi:clock-outline" },
        { id: "smileys", name: "Smileys & People", icon: "mdi:emoticon-happy-outline" },
        { id: "animals", name: "Animals & Nature", icon: "mdi:dog" },
        { id: "food", name: "Food & Drink", icon: "mdi:food" },
        { id: "travel", name: "Travel & Places", icon: "mdi:airplane" },
        { id: "activities", name: "Activities", icon: "mdi:soccer" },
        { id: "objects", name: "Objects", icon: "mdi:lightbulb-outline" },
        { id: "symbols", name: "Symbols", icon: "mdi:percent" },
        { id: "flags", name: "Flags", icon: "mdi:flag" },
      ],
      emojiData: EMOJI_CATEGORIES,
      recentEmojis: this.loadRecentEmojis(),
    };
  },
  computed: {
    filteredCategories() {
      if (this.activeCategory === "recent") {
        return [
          {
            id: "recent",
            name: "Recently Used",
            emojis: this.recentEmojis.slice(0, 50),
          },
        ];
      }

      const categoryEmojis = this.emojiData[this.activeCategory] || [];
      return [
        {
          id: this.activeCategory,
          name: this.categories.find((c) => c.id === this.activeCategory)?.name || "",
          emojis: categoryEmojis,
        },
      ];
    },
    searchResults() {
      if (!this.searchQuery) return [];

      const query = this.searchQuery.toLowerCase();
      const results = [];

      Object.keys(this.emojiData).forEach((categoryKey) => {
        if (categoryKey === "recent") return;
        const categoryEmojis = this.emojiData[categoryKey] || [];
        categoryEmojis.forEach((emoji) => {
          if (emoji.name.toLowerCase().includes(query)) {
            results.push(emoji);
          }
        });
      });

      return results.slice(0, 50);
    },
  },
  methods: {
    selectEmoji(emoji) {
      this.addToRecent(emoji);
      this.$emit("select", emoji);
    },
    addToRecent(emoji) {
      const recent = this.recentEmojis.filter((e) => e.emoji !== emoji.emoji);
      recent.unshift(emoji);
      this.recentEmojis = recent.slice(0, 50);
      this.saveRecentEmojis();
    },
    loadRecentEmojis() {
      try {
        const stored = localStorage.getItem("emoji-recent");
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (error) {
        return [];
      }
      return [];
    },
    saveRecentEmojis() {
      try {
        localStorage.setItem("emoji-recent", JSON.stringify(this.recentEmojis));
      } catch (error) {
        // Ignore storage errors
      }
    },
  },
};
</script>

<style scoped>
.emoji-picker {
  width: 420px;
  height: 400px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.emoji-picker__search {
  padding: 6px;
  border-bottom: 1px solid #e5e7eb;
}

.emoji-picker__search-input {
  width: 100%;
}

.emoji-picker__categories {
  display: flex;
  gap: 4px;
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
  overflow-x: auto;
}

.emoji-picker__category-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s;
  flex-shrink: 0;
}

.emoji-picker__category-btn:hover {
  background: #f3f4f6;
  color: #3e3efe;
}

.emoji-picker__category-btn--active {
  background: #f8f7ff;
  color: #3e3efe;
}

.emoji-picker__content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.emoji-picker__category-section {
  margin-bottom: 16px;
}

.emoji-picker__category-title {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  margin-bottom: 8px;
  padding: 0 4px;
}

.emoji-picker__emoji-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 4px;
}

.emoji-picker__search-results {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 4px;
}

.emoji-picker__emoji {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 20px;
  transition: all 0.2s;
  padding: 0;
}

.emoji-picker__emoji:hover {
  background: #f3f4f6;
  transform: scale(1.1);
}
</style>
